"use client";

import { useState, useEffect } from "react";
import { format, addDays } from "date-fns";
import { ChevronLeft, ChevronRight, Check, Calendar, Mail, User, ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Turnstile } from "@/components/Turnstile";
import { getStoredConsent } from "@/components/common/ConsentBanner";
import { NationalitySelect } from "@/components/common/NationalitySelect";
import { isRestrictedNationality } from "@/lib/nationality";

const API_BASE_URL = process.env.NEXT_PUBLIC_CMS_API_URL || "http://localhost:3001";

async function getVisitTimeSlots() {
  const res = await fetch(`${API_BASE_URL}/api/visits/slots`);
  if (!res.ok) throw new Error("Failed to fetch slots");
  return res.json();
}

async function getBookedSlots(propertyId: string, visitDate: string) {
  const query = new URLSearchParams({ propertyId, visitDate }).toString();
  const res = await fetch(`${API_BASE_URL}/api/visits/booked-slots?${query}`);
  if (!res.ok) throw new Error("Failed to fetch booked slots");
  return res.json();
}

async function scheduleVisit(data: any) {
  const res = await fetch(`${API_BASE_URL}/api/visits/schedule`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.text();
    return { success: false, error: err || "Failed to schedule visit" };
  }
  return { success: true };
}

const FALLBACK_SLOTS = [
  { value: '10:00-13:00', label: '10:00 am \u2013 01:00 pm' },
  { value: '13:00-16:00', label: '01:00 pm \u2013 04:00 pm' },
  { value: '16:00-19:00', label: '04:00 pm \u2013 07:00 pm' },
];

interface ScheduleVisitWidgetProps {
  propertyId: string;
  propertyTitle?: string;
}

type WidgetStep = "datetime" | "form" | "confirmed";

export function ScheduleVisitWidget({ propertyId, propertyTitle }: ScheduleVisitWidgetProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build visible dates: today + 6 days
  const buildDates = () => {
    return Array.from({ length: 7 }, (_, i) => addDays(today, i));
  };

  const [step, setStep] = useState<WidgetStep>("datetime");
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<{ value: string, label: string }[]>(FALLBACK_SLOTS as any);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string>(FALLBACK_SLOTS[0].value);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch all slots once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const slots = await getVisitTimeSlots();
        if (mounted && slots && slots.length > 0) {
          const formatted = slots.map((s: any) => ({
            value: `${s.start_time.slice(0, 5)}-${s.end_time.slice(0, 5)}`,
            label: s.label
          }));
          setTimeSlots(formatted);
        }
      } catch (err) {
        console.error("Error loading slots:", err);
      } finally {
        if (mounted) setIsLoadingSlots(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fetch booked slots when date changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoadingSlots(true);
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const booked = await getBookedSlots(propertyId, dateStr);
        if (mounted) {
          setBookedSlots(booked);
        }
      } catch (err) {
        console.error("Error fetching booked slots:", err);
      } finally {
        if (mounted) setIsLoadingSlots(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedDate, propertyId]);

  // Update selected slot if current one becomes unavailable or if slots list changes
  useEffect(() => {
    const availableSlots = timeSlots.filter(s => !bookedSlots.includes(s.value));
    if (availableSlots.length > 0) {
      if (!availableSlots.find(s => s.value === selectedSlot)) {
        setSelectedSlot(availableSlots[0].value);
      }
    } else {
      setSelectedSlot("");
    }
  }, [bookedSlots, timeSlots]);

  const [consent, setConsent] = useState(true);
  const [dateOffset, setDateOffset] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visitType, setVisitType] = useState<"in_person" | "video">("in_person");

  // Form fields
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [nationality, setNationality] = useState("");
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string; nationality?: string }>({});

  const dates = buildDates();
  const visibleDates = dates.slice(dateOffset, dateOffset + 5);

  const formatDateLabel = (date: Date) => {
    const isToday = date.toDateString() === today.toDateString();
    if (isToday) return { day: "Today", date: format(date, "d") };
    return { day: format(date, "EEE"), date: format(date, "d") };
  };

  const handleScheduleClick = () => {
    if (!consent) return;
    setSubmitError(null);
    setStep("form");
  };

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!fullName.trim()) newErrors.name = "Name is required";
    if (!phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^\+?\d{8,15}$/.test(phone.trim().replace(/[\s-]/g, ''))) newErrors.phone = "Enter a valid mobile number";
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Enter a valid email address";
    }
    if (!nationality) newErrors.nationality = "Nationality is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validate()) return;
    // Restricted nationalities are not offered a visit either (no property available).
    if (isRestrictedNationality(nationality)) return;
    if (!turnstileToken) {
      setSubmitError("Please complete the CAPTCHA verification.");
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await scheduleVisit({
        propertyId,
        propertyTitle,
        visitorName: fullName.trim(),
        visitorPhone: phone.trim(),
        visitorEmail: email.trim() || undefined,
        visitDate: format(selectedDate, "yyyy-MM-dd"),
        timeSlot: selectedSlot,
        consentGiven: consent,
        visitType: visitType,
        nationality,
        turnstileToken,
        // Only persist the IP if the visitor has explicitly accepted data storage
        // (null = not answered yet → do not store).
        storeIp: getStoredConsent() === 'accepted',
      });

      if (result.success) {
        setStep("confirmed");
        setTurnstileToken(null);
      } else {
        setSubmitError(result.error || "Failed to schedule visit. Please try again.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedSlotLabel = timeSlots.find(s => s.value === selectedSlot)?.label || selectedSlot;

  // ── Confirmed State ──
  if (step === "confirmed") {
    return (
      <div className="p-4 flex flex-col items-center gap-4 text-center">
        <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground mb-1">Visit Scheduled!</h3>
          <p className="text-sm text-muted-foreground">
            We'll contact you to confirm your visit.
          </p>
        </div>
        <div className="w-full bg-muted/50 rounded-xl p-4 text-left space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-primary shrink-0" />
            <span className="font-medium">{format(selectedDate, "EEEE, d MMMM yyyy")}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-primary shrink-0" />
            <span className="font-medium">{selectedSlotLabel}</span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full rounded-xl"
          onClick={() => {
            setStep("datetime");
            setFullName("");
            setPhone("");
            setEmail("");
            setErrors({});
          }}
        >
          Schedule Another Visit
        </Button>
      </div>
    );
  }

  // ── Form Step ──
  if (step === "form") {
    return (
      <div className="p-4 flex flex-col gap-4">
        {/* Back link */}
        <button
          onClick={() => setStep("datetime")}
          className="flex items-center gap-1 text-sm text-primary hover:underline w-fit"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Edit Date &amp; Time
        </button>

        {/* Selected date/slot summary */}
        <div className="bg-muted/50 rounded-xl p-3 flex items-center gap-3">
          <div className="flex-1 text-sm">
            <p className="font-semibold text-foreground">{format(selectedDate, "EEE, d MMM yyyy")}</p>
            <p className="text-muted-foreground text-xs mt-0.5">{selectedSlotLabel}</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-primary" />
          </div>
        </div>

        <p className="text-xs text-muted-foreground italic">
          Directions to the house will be sent via SMS or email.
        </p>

        {/* Form fields */}
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium mb-1.5 block">Visit Type *</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setVisitType("in_person")}
                className={cn(
                  "flex items-center justify-center gap-2 py-2 px-3 text-sm rounded-xl border transition-all",
                  visitType === "in_person"
                    ? "border-primary bg-primary/5 text-primary font-medium"
                    : "border-border bg-background text-muted-foreground hover:bg-muted/50"
                )}
              >
                In Person
              </button>
              <button
                onClick={() => setVisitType("video")}
                className={cn(
                  "flex items-center justify-center gap-2 py-2 px-3 text-sm rounded-xl border transition-all",
                  visitType === "video"
                    ? "border-primary bg-primary/5 text-primary font-medium"
                    : "border-border bg-background text-muted-foreground hover:bg-muted/50"
                )}
              >
                Video Meeting
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="sv-name" className="text-xs font-medium">Full Name *</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="sv-name"
                value={fullName}
                onChange={e => { setFullName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
                placeholder="Enter your full name"
                className={cn("pl-9 h-10 text-sm rounded-xl", errors.name && "border-red-500 focus-visible:ring-red-500")}
              />
            </div>
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <Label htmlFor="sv-phone" className="text-xs font-medium">Phone Number *</Label>
            <div className="relative mt-1">
              <PhoneInput
                id="sv-phone"
                defaultCountry="AE"
                value={phone}
                onChange={val => { setPhone(val ? String(val) : ""); setErrors(p => ({ ...p, phone: undefined })); }}
                placeholder="Mobile number"
                className={cn("h-10", errors.phone && "border-red-500 focus-within:ring-red-500/30")}
              />
            </div>
            {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
          </div>

          <div>
            <Label htmlFor="sv-email" className="text-xs font-medium">Email <span className="text-muted-foreground">(Optional)</span></Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="sv-email"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: undefined })); }}
                placeholder="your@email.com"
                className={cn("pl-9 h-10 text-sm rounded-xl", errors.email && "border-red-500 focus-visible:ring-red-500")}
              />
            </div>
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <Label htmlFor="sv-nationality" className="text-xs font-medium">Nationality</Label>
            <div className="mt-1">
              <NationalitySelect
                id="sv-nationality"
                value={nationality}
                onChange={(v) => { setNationality(v); setErrors(p => ({ ...p, nationality: undefined })); }}
              />
            </div>
            {errors.nationality && <p className="text-xs text-red-500 mt-1">{errors.nationality}</p>}
          </div>
        </div>

        <Turnstile
          onVerify={(token) => setTurnstileToken(token)}
          onExpire={() => setTurnstileToken(null)}
          onError={() => setTurnstileToken(null)}
          className="my-1"
        />

        {submitError && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-xs font-medium animate-in fade-in duration-300">
            {submitError}
          </div>
        )}

        {isRestrictedNationality(nationality) ? (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
            <p>Thank you for your interest. We&apos;ve noted your details and our team will reach out to you.</p>
          </div>
        ) : (
          <Button
            onClick={handleFormSubmit}
            disabled={isSubmitting}
            className="w-full h-11 rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isSubmitting ? "Scheduling..." : "Confirm Visit"}
          </Button>
        )}
      </div>
    );
  }

  // ── Date & Time Step ──
  return (
    <div className="px-4 sm:px-5 flex flex-col gap-2 bg-gradient-to-b from-transparent to-primary/5 rounded-b-2xl">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Clock className="w-3.5 h-3.5 text-primary" />
        </div>
        <h3 className="text-sm font-bold text-foreground tracking-tight">Schedule a Free Tour</h3>
      </div>

      {/* Date Chips */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setDateOffset(d => Math.max(0, d - 1))}
            disabled={dateOffset === 0}
            aria-label="Previous dates"
            className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors shrink-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex gap-1.5 flex-1 overflow-hidden">
            {visibleDates.map((date) => {
              const isSelected = date.toDateString() === selectedDate.toDateString();
              const { day, date: dateNum } = formatDateLabel(date);
              return (
                <button
                  key={date.toISOString()}
                  onClick={() => setSelectedDate(date)}
                  className={cn(
                    "flex-1 flex flex-col items-center py-1.5 px-1 rounded-lg border text-xs font-medium transition-all min-w-[40px] cursor-pointer",
                    isSelected
                      ? "border-primary bg-primary text-primary-foreground shadow-md shadow-primary/20 scale-105 z-10"
                      : "border-border hover:border-primary/40 hover:bg-muted"
                  )}
                >
                  <span className="text-[9px] opacity-80 mb-0.5 uppercase tracking-wider">{day}</span>
                  <span className="font-bold text-sm leading-none">{dateNum}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setDateOffset(d => Math.min(2, d + 1))}
            disabled={dateOffset >= 2}
            aria-label="Next dates"
            className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors shrink-0"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Time Slots */}
      <div className="flex flex-col gap-2 relative min-h-[100px]">
        {isLoadingSlots && (
          <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center rounded-xl">
            <span className="text-xs text-muted-foreground animate-pulse">Checking availability...</span>
          </div>
        )}

        {timeSlots.filter(s => !bookedSlots.includes(s.value)).length > 0 ? (
          timeSlots.filter(s => !bookedSlots.includes(s.value)).map((slot) => {
            const isSelected = slot.value === selectedSlot;
            return (
              <button
                key={slot.value}
                onClick={() => setSelectedSlot(slot.value)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 rounded-lg border text-xs font-semibold transition-all cursor-pointer",
                  isSelected
                    ? "border-primary bg-primary/10 text-primary shadow-inner"
                    : "border-border hover:border-primary/40 hover:bg-background bg-background/50"
                )}
              >
                <span>{slot.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5" />}
              </button>
            );
          })
        ) : (
          !isLoadingSlots && (
            <div className="p-8 text-center border border-dashed border-border rounded-xl">
              <Clock className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground">No slots available</p>
              <p className="text-xs text-muted-foreground mt-1">Please pick another date.</p>
            </div>
          )
        )}
      </div>

      {/* Consent */}
      {/* <label className="flex items-start gap-2 cursor-pointer group">
        <div
          className={cn(
            "mt-0.5 w-4 h-4 rounded border-2 shrink-0 flex items-center justify-center transition-colors",
            consent ? "border-primary bg-primary" : "border-border group-hover:border-primary/50"
          )}
          onClick={() => setConsent(c => !c)}
        >
          {consent && <Check className="w-2.5 h-2.5 text-white" />}
        </div>
        <span className="text-[10px] text-muted-foreground leading-tight pt-0.5">
          I agree to be contacted as per Privacy Policy and T&amp;C.
        </span>
      </label> */}

      {/* CTA */}
      <Button
        onClick={handleScheduleClick}
        disabled={!consent || !selectedSlot}
        className="w-full h-10 rounded-xl font-bold bg-primary hover:bg-primary/90 text-background disabled:opacity-50 mt-1 shadow-md hover:shadow-lg transition-all"
      >
        {selectedSlot ? "Schedule Visit" : "No Slots Available"}
      </Button>
    </div>
  );
}
